import { Injectable, OnDestroy } from '@angular/core';
import { BookedTrip } from '@ibe.models/booked-trip.model.lib';
import { PassengerFlightInfo } from '@ibe/models';
import { getBrandFeatures } from '@ibe/models/ui';
import * as TotalSummaryActions from '@ibe/store/actions/booking-total-summary.actions';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { takeWhile } from 'rxjs/operators';
import * as fromStore from '../store';


@Injectable()
export class BookingTotalSummaryEffect implements OnDestroy {

	private booked_trip$: Observable<BookedTrip> = this.store.select(fromStore.getSelectedBookedTrip);
	private isAlive = true;

	constructor(
		private store: Store<fromStore.State>,
	) {
	}

	ngOnDestroy() {
		this.isAlive = false;
	}

	putTotalSummary() {
		this.store.dispatch(new TotalSummaryActions.LoadingAction);
		this.booked_trip$
			.pipe(takeWhile(() => this.isAlive))
			.subscribe(booked_trip => {
				if (booked_trip) {
					const totalSummary = this.createTotalSummary(booked_trip);
					this.store.dispatch(new TotalSummaryActions.PutItensSummary(totalSummary));
				}
			});
	}

	/**
	 *
	 *
	 */
	createTotalSummary(booked_trip: BookedTrip) {

		const filterFnSum = (passengerInfo: PassengerFlightInfo, item: string): number => {
			const stops = passengerInfo.stops.filter(accPass => accPass.added && accPass.added[item]);
			return stops.reduce((accPass, pass) => accPass += pass.added[item].price, 0);
		};

		const filterFnCount = (passengerInfo: PassengerFlightInfo, item: string): number => {
			const stops = passengerInfo.stops.filter(accPass => accPass.added && accPass.added[item]);
			return stops.reduce((accPass, pass) => accPass += 1, 0);
		};

		const filterFnSumFn = (passengerInfo: PassengerFlightInfo, item: string): number => {
			const stops = passengerInfo.stops.filter(accPass => accPass.added && accPass.added[item]);
			return stops.reduce((accPass, pass) => accPass += pass.added[item].reduce((acc, cur) => acc += cur.price, 0), 0);
		};

		const filterFnCountFn = (passengerInfo: PassengerFlightInfo, item: string): number => {
			const stops = passengerInfo.stops.filter(accPass => accPass.added && accPass.added[item]);
			return stops.reduce((accPass, pass) => accPass += pass.added[item].reduce((acc, cur) => acc += 1, 0), 0);
		};

		const summaryFlights = booked_trip.flights.reduce((accFlight, flight) => {
			const summary = [];
			const brand = getBrandFeatures(flight.options.brand);

			/**
			 * Tickets
			 */
			summary.push({ item: 'tickets', label: 'Flights', total: flight.passengerInfo.length, value: (flight.passengerInfo.length * flight.options.price).toFixed(2), sort: 10 });

			/**
			 * Seats
			 */
			const seatsSum: number = flight.passengerInfo.reduce((accPass, cur) => accPass += filterFnSum(cur, 'boughtSeat'), 0);
			const seatsCount: number = flight.passengerInfo.reduce((accPass, cur) => accPass += filterFnCount(cur, 'boughtSeat'), 0);
			if (seatsSum) summary.push({ item: 'seats', total: seatsCount, label: 'Seats', value: seatsSum.toFixed(2), sort: 20 });

			/**
			 * Extra bags
			 */
			const extraBagsSum: number = flight.passengerInfo.reduce((accPass, pass) =>
				accPass += pass.stops
					.filter(cur => cur.added && cur.added.added_bags)
					.reduce((accStop, stop) => accStop += stop.added.added_bags.reduce((accBags, bag) => accBags += bag, 0), 0)
				, 0);

			const extraBagsCount: number = flight.passengerInfo.reduce((accPass, pass) => accPass += pass.stops.filter(cur => cur.added && cur.added.added_bags).reduce((accStop, stop) => accStop += stop.added.added_bags.length, 0), 0);
			if (extraBagsSum) summary.push({ item: 'extra-bags', total: extraBagsCount, label: 'Extra Bags', value: extraBagsSum.toFixed(2), sort: 30 });

			/**
			 * Fast track
			 */
			const brandsFasttrack = brand.filter(cur => cur.type == 'extra').find(cur => cur.id == 'fasttrack');
			const priceFasttrack = brandsFasttrack && brandsFasttrack.price ? brandsFasttrack.price : 0;
			const sumPassengerWithFastTrack = flight.passengerInfo.reduce((accPass, pass) => accPass += pass.stops.filter(cur => cur.added && cur.added.fastTrackEnabled).length, 0);
			const fastTrackSum = sumPassengerWithFastTrack * priceFasttrack;
			if (fastTrackSum) summary.push({ item: 'fasttrack', total: sumPassengerWithFastTrack, label: 'Fast Track', value: fastTrackSum.toFixed(2), sort: 40 });

			/**
			 * Lounge
			 */
			const brandsLounge = getBrandFeatures(flight.options.brand).filter(cur => cur.type == 'extra').find(cur => cur.id == 'lounge');
			const priceLounge = brandsLounge && brandsLounge.price ? brandsLounge.price : 0;
			const sumPassenge = flight.passengerInfo.reduce((accPass, pass) => accPass += pass.stops.filter(cur => cur.added && cur.added.loungeEnabled).length, 0);
			const loungeSum = sumPassenge * priceLounge;
			if (loungeSum) summary.push({ item: 'lounge', total: sumPassenge, label: 'Lounge', value: loungeSum.toFixed(2), sort: 50 });


			/**
			 * Sports Equipments
			 */
			const sportEquipmentsSum: number = flight.passengerInfo.reduce((accPass, cur) => accPass += filterFnSumFn(cur, 'sportsEquipment'), 0);
			const sportEquipmentsCount: number = flight.passengerInfo.reduce((accPass, cur) => accPass += filterFnCountFn(cur, 'sportsEquipment'), 0);
			if (sportEquipmentsSum) summary.push({ item: 'sport-equipments', total: sportEquipmentsCount, label: 'Sport Equipments', value: sportEquipmentsSum.toFixed(2), sort: 60 });

			/**
			 * Music Instruments
			 */
			const musicInstrumentsSum: number = flight.passengerInfo.reduce((accPass, cur) => accPass += filterFnSumFn(cur, 'musicInstruments'), 0);
			const musicInstrumentsCount: number = flight.passengerInfo.reduce((accPass, cur) => accPass += filterFnCountFn(cur, 'musicInstruments'), 0);
			if (musicInstrumentsSum) summary.push({ item: 'music-instruments', total: musicInstrumentsCount, label: 'Music Instruments', value: musicInstrumentsSum.toFixed(2), sort: 70 });

			/**
			 * Pets
			 */
			const petsSum: number = flight.passengerInfo.reduce((accPass, cur) => accPass += filterFnSumFn(cur, 'pets'), 0);
			const petsCount: number = flight.passengerInfo.reduce((accPass, cur) => accPass += filterFnCountFn(cur, 'pets'), 0);
			if (petsSum) summary.push({ item: 'pets', label: 'Pets', total: petsCount, value: petsSum.toFixed(2), sort: 80 });

			/**
			 * Special Meals
			 */
			const mealsSum: number = flight.passengerInfo.reduce((accPass, cur) => accPass += filterFnSum(cur, 'specialMeals'), 0);
			const mealsCount: number = flight.passengerInfo.reduce((accPass, cur) => accPass += filterFnCount(cur, 'specialMeals'), 0);
			const mealsAdded = flight.passengerInfo.reduce((accPass, passenger) => accPass += passenger.stops.filter(stop => stop.added && stop.added.specialMeals).length, 0);
			if (mealsAdded) summary.push({ item: 'special-meals', total: mealsCount, label: 'Special Meals', value: mealsSum.toFixed(2), sort: 90 });

			/**
			 * Taxes
			 */
			const sumSummary = summary.reduce((acc, cur) => acc += parseFloat(cur.value), 0);

			summary.push({ item: 'taxes', label: 'Taxes', total: 1, value: (sumSummary * 0.25).toFixed(2), sort: 100 });
			summary.sort((a, b) => a.sort - b.sort);

			accFlight.push({ flight, summary });

			return accFlight;
		}, []);

		const sumSummaryFlights = summaryFlights
			.reduce((acc, cur) => acc.concat(cur.summary), [])
			.reduce((acc, cur) => {
				cur.value = parseFloat(cur.value);
				cur.total = parseFloat(cur.total);
				const item = acc.find(c => c.item == cur.item);
				if (item) {
					item.value += parseFloat(cur.value);
					item.total += parseFloat(cur.total);
				} else {
					acc.push(cur);
				}
				return acc;

			}, [])
			.map(cur => {
				cur.value = parseFloat(cur.value).toFixed(2);
				cur.total = parseFloat(cur.total).toFixed(2);
				return cur;
			});


		/**
		 * Insurance
		 */
		const insurance = booked_trip.insurance;
		if (insurance) {
			sumSummaryFlights.push({
				item: 'insurance', label: 'Insurance', total: booked_trip.passengerCount, value: (booked_trip.passengerCount * insurance.reduce((acc, cur) => acc += cur.price, 0)).toFixed(2), sort: 100
			});
			sumSummaryFlights.sort((a, b) => a.sort - b.sort);
		}

		const totalSummary = {
			flights: summaryFlights,
			summary: sumSummaryFlights
		};

		return totalSummary;

	}


}
